/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';

import api from '../api/api';
import { IotDeviceDetail } from '../types/iot';
import { getAuthCookies } from '../utils/authTokens';

export type TerminalStatus =
 | 'idle'
 | 'loading'
 | 'streaming'
 | 'error';

export interface TerminalFrame {
 mediaType: string;
 base64: string;
}

const API_BASE = String(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');

/**
 * Driver-terminal screen stream controller.
 *
 * On a device id it loads GET /iot/v1/{id} for the IotSource.UID, then opens a
 * Server-Sent-Events stream at
 *   {API_BASE}/special/v1/DriverTerminalScreenOutput/{UID}
 * consumed via fetch + ReadableStream so the app's Bearer access token can be
 * attached (native EventSource cannot set headers). Exposes the latest event
 * payload.
 */
export const useDriverTerminalStream = (deviceId?: number | null) => {
 const [status, setStatus] = useState<TerminalStatus>('idle');
 const [name, setName] = useState('');
 const [error, setError] = useState<string | null>(null);
 const [frame, setFrame] = useState<TerminalFrame | null>(null);

 const abortRef = useRef<AbortController | null>(null);
 const urlRef = useRef('');

 const stop = useCallback(() => {
  if (abortRef.current) {
   try {
    abortRef.current.abort();
   } catch {
    /* noop */
   }
   abortRef.current = null;
  }
 }, []);

 const start = useCallback(() => {
  const url = urlRef.current;
  if (!url) return;
  stop();

  const token = getAuthCookies().accessToken || '';
  const controller = new AbortController();
  abortRef.current = controller;

  const run = async () => {
   try {
    setError(null);
    setStatus('loading');
    const resp = await fetch(url, {
     method: 'GET',
     headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
     },
     signal: controller.signal,
    });
    if (!resp.ok || !resp.body) {
     throw new Error(`Stream HTTP ${resp.status}`);
    }
    setStatus('streaming');

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Parse SSE frames: events are separated by a blank line; payload lines
    // start with "data:". Multiple data: lines in one event are joined by \n.
    // Each payload is JSON: { mediaType, base64 }.
    const flush = (raw: string) => {
     const dataLines = raw
      .split('\n')
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).replace(/^ /, ''));
     if (!dataLines.length) return;
     const payload = dataLines.join('\n').trim();
     if (!payload) return;
     try {
      const obj = JSON.parse(payload);
      if (obj && obj.base64) {
       setFrame({
        mediaType: String(obj.mediaType || 'image/png'),
        base64: String(obj.base64),
       });
      }
     } catch {
      /* ignore non-JSON keep-alive / partial frames */
     }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
     // eslint-disable-next-line no-await-in-loop
     const { done, value } = await reader.read();
     if (done) break;
     buffer += decoder.decode(value, { stream: true });
     let sep = buffer.indexOf('\n\n');
     while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      flush(frame);
      sep = buffer.indexOf('\n\n');
     }
    }
    // Stream ended cleanly.
    if (abortRef.current === controller) {
     abortRef.current = null;
     setStatus('idle');
    }
   } catch (e: any) {
    if (controller.signal.aborted) return;
    setStatus('error');
    setError(e?.message ?? 'Stream failed');
   }
  };

  run();
 }, [stop]);

 // Stop the stream when the tab is hidden / page hides.
 useEffect(() => {
  const onHidden = () => {
   if (document.visibilityState === 'hidden') stop();
  };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', stop);
  return () => {
   document.removeEventListener('visibilitychange', onHidden);
   window.removeEventListener('pagehide', stop);
  };
 }, [stop]);

 // Load device detail + open the stream whenever the selected device changes.
 useEffect(() => {
  let cancelled = false;
  stop();
  urlRef.current = '';
  setFrame(null);
  setError(null);

  if (!deviceId) {
   setStatus('idle');
   setName('');
   return undefined;
  }

  const run = async () => {
   try {
    setStatus('loading');
    const { data: detailData } = await api.get(`/iot/v1/${deviceId}`);
    if (cancelled) return;
    const detail = detailData as IotDeviceDetail;
    setName(detail?.Name ?? '');

    const uid = detail?.IotSource?.UID;
    if (!uid) {
     setStatus('error');
     setError('Missing IotSource.UID');
     return;
    }
    urlRef.current = `${API_BASE}/special/v1/DriverTerminalScreenOutput/${encodeURIComponent(
     uid,
    )}`;
    // Auto-start streaming as soon as the terminal is selected.
    start();
   } catch (e: any) {
    if (cancelled) return;
    setStatus('error');
    setError(e?.message ?? 'Failed to load device');
   }
  };

  run();

  return () => {
   cancelled = true;
   stop();
  };
 }, [deviceId, stop, start]);

 return { status, name, error, frame, start, stop };
};

export default useDriverTerminalStream;
